export const CUSTOM_THEME_CONFIG = {
    // API Configuration
    api: {
        // Base URL for custom template API
        baseUrl: process.env.NEXT_PUBLIC_API_BASE_URL || 'https://api.aff.coderlabdev.com/api',

        // Template endpoints
        endpoints: {
            custom_template: '/company/get_custom_template',
            blog: '/company/get_blog',
            products: '/company/get_products',
            categories: '/company/get_categories',
        },
    },

    // Company & Template IDs
    template: {
        companyId: process.env.NEXT_PUBLIC_COMPANY_ID || 'Cv0t050517251730',
        templateId: process.env.NEXT_PUBLIC_TEMPLATE_ID || 'cusdsdsdfsfd540025',
    },

    // Page Slugs - Map your page names to template slugs
    pages: {
        home: 'home_page',
        products: 'products_page',
        about: 'about_us',
        contact: 'contact_page',
        blog: 'blog_page',
        category: 'category_page',
        categorySpecific: 'category_specific_page',
        productsSpecific: 'products_specific_page',
        allProductsSpecific: 'all_products_specific_page',
        eventsSpecific: 'event_specific_page',
        promotionSpecific: 'promotion_specific_page',
        storeSpecific: 'store_specific_page',
        allProducts: 'all_products_page',
        allStores: 'store_page',
        search: 'search_page',
        events: 'events_page',
        promotions: 'promotion_page',
        privacyPolicy: 'privacy_policy_page',
        termsOfServices: 'terms_of_services_page',
        affiliateDisclosure: 'affiliate_disclosure_page',
        doNotSellMyPersonalInformation: 'do_not_sell_my_personal_information_page',
        other: 'other_page',
    },

    // Component Configuration
    components: {
        // Default classes for different component types
        defaults: {
            card: {
                className: 'rounded-lg overflow-hidden border border-gray-200',
            },
            button: {
                className: 'px-4 py-2 rounded-full font-medium hover:opacity-90 transition-opacity',
            },
            container: {
                className: 'w-full',
            },
        },

        // Component type mapping
        types: [
            'H1',
            'H2',
            'H3',
            'H4',
            'P',
            'TEXT',
            'BUTTON',
            'IMAGE',
            'CARD',
            'CONTAINER',
            'COLUMN',
            'ROW',
            'GRID',
            'SECTION',
            'BlogSection',
        ],
    },

    // Style Configuration
    styles: {
        // Default responsive breakpoints
        breakpoints: {
            xs: 0,
            sm: 640,
            md: 768,
            lg: 1024,
            xl: 1280,
            '2xl': 1536,
        },

        // Default grid gap values
        gridGaps: {
            small: 12,
            medium: 24,
            large: 36,
            xlarge: 48,
        },
    },

    // Feature Flags
    features: {
        // Enable/disable data caching
        enableCache: true,
        cacheExpiration: 60 * 60 * 1000, // 1 hour in milliseconds

        // Enable/disable error boundaries
        enableErrorBoundary: true,

        // Enable/disable loading skeletons
        enableLoadingSkeletons: true,

        // Enable/disable API request logging
        enableRequestLogging: process.env.NODE_ENV === 'development',
    },

    // Loading States
    loading: {
        // Show loading message
        showLoadingMessage: true,
        loadingMessage: 'Loading page...',

        // Loading timeout (ms)
        timeout: 10000,
    },

    // Error Handling
    errors: {
        // Show detailed error messages
        showDetailedErrors: process.env.NODE_ENV === 'development',

        // Retry configuration
        retryAttempts: 3,
        retryDelay: 1000, // ms
    },
};

/**
 * Get API base URL
 */
export function getApiBaseUrl(): string {
    return CUSTOM_THEME_CONFIG.api.baseUrl;
}

/**
 * Get template endpoint
 */
export function getTemplateEndpoint(): string {
    const baseUrl = getApiBaseUrl();
    const endpoint = CUSTOM_THEME_CONFIG.api.endpoints.custom_template;
    return `${baseUrl}${endpoint}`;
}

/**
 * Get page slug by page name
 */
export function getPageSlug(pageName: keyof typeof CUSTOM_THEME_CONFIG.pages): string {
    return CUSTOM_THEME_CONFIG.pages[pageName];
}

/**
 * Get company ID
 */
export function getCompanyId(): string {
    return CUSTOM_THEME_CONFIG.template.companyId;
}

/**
 * Get template ID
 */
export function getTemplateId(): string {
    return CUSTOM_THEME_CONFIG.template.templateId;
}

/**
 * Check if component type is supported
 */
export function isSupportedComponentType(type: string): boolean {
    return CUSTOM_THEME_CONFIG.components.types.includes(type as any);
}

/**
 * Get default class names for component type
 */
export function getComponentDefaults(type: string): any {
    const defaults: any = CUSTOM_THEME_CONFIG.components.defaults;
    return defaults[type.toLowerCase()] || {};
}
